import { action, observable, runInAction } from 'mobx';
import { Container, Inject } from 'typescript-ioc';
import { HubModuleLayout, HubModuleType, ICreateSpeakersHubModuleDTO, ISpeakerReferenceDTO, ISpeakersHubModuleDTO, } from '../../../../../../../../api/event-hubs.dao';
import { IAccountContactDTO, IAccountContactListItemDTO, IAccountContactsDAO } from '../../../../../../../../api/account-contacts.dao';
import { AccountContactsDAO } from '../../../../../../../../api/implementations/account-contacts.dao';
import { IErrorParsing } from '../../../../../../../../infrastructure/error-parsing';
import { alertServerError } from '../../../../../../../global/modal/alerts-host/alert-templates';
import { errorText } from '../../../../../../../../constants/common-labels.constants';
import { IRouter } from '../../../../../../../../infrastructure/router';
import { RouteParams } from '../../../../../../../../routes-table';
import { RADIX_PARAM } from '../../../../../../../../constants/common.constants';
import { EventName, IEventDispatcher } from '../../../../../../../../infrastructure/event-dispatcher';
import {
    DESCRIPTION,
    DISPLAY_BIO,
    DISPLAY_LABEL,
    DISPLAY_NAME,
    DISPLAY_ORG,
    DISPLAY_PROFILE_IMAGE,
    DISPLAY_TITLE,
    LABEL,
    LAYOUT,
    POSITION_ORDER,
    SPEAKERS_MODULE_CONFIG,
} from './speakers-module.config';
import { HubModuleVM } from '../hub-module.viewmodel';
import { transpileClassesToInlineStyles, transpileInlineStylesToClasses } from '../../../../../../../global/rich-text-editor/utils/transpiler';

export class SpeakersModuleVM extends HubModuleVM {
    private static sortByPositionOrder(speakers: ISpeakerReferenceDTO[]): ISpeakerReferenceDTO[] {
        return speakers.map(speaker => ({ ...speaker })).sort((a, b) => a.positionOrder - b.positionOrder);
    }

    public static createEmpty(positionOrder: number): SpeakersModuleVM {
        return new SpeakersModuleVM({ positionOrder: positionOrder });
    }

    public static createFromDTO(dto: ISpeakersHubModuleDTO | ICreateSpeakersHubModuleDTO): SpeakersModuleVM {
        const transpiledDescription = transpileInlineStylesToClasses(dto.description);
        return new SpeakersModuleVM({ ...dto, description: transpiledDescription });
    }

    private constructor(
        module: Partial<ISpeakersHubModuleDTO>,
        @Inject private readonly routingService: IRouter<RouteParams> = Container.get(IRouter),
        @Inject private readonly accountContactsDAO: IAccountContactsDAO = Container.get(AccountContactsDAO),
        @Inject private readonly errorParsingService: IErrorParsing = Container.get(IErrorParsing),
        @Inject private readonly eventDispatcher: IEventDispatcher = Container.get(IEventDispatcher),
    ) {
        super(module.moduleId, 'SpeakersModuleVM', SPEAKERS_MODULE_CONFIG, module);
        this.speakers = SpeakersModuleVM.sortByPositionOrder(module.speakers ?? []);
        this.savedSnapshot = this.serialize();
        this.eventDispatcher.registerHandler(EventName.SpeakerUpdated, this.handleSpeakerUpdated);
    }

    public dispose(): void {
        this.eventDispatcher.unregisterHandler(EventName.SpeakerUpdated, this.handleSpeakerUpdated);
    }

    @observable
    public isAddSpeakerModalOpen: boolean = false;

    @observable
    public editingSpeaker: ISpeakerReferenceDTO | null = null;

    @observable
    public speakers: ISpeakerReferenceDTO[] = [];

    @observable
    public contacts: IAccountContactListItemDTO[] = [];

    @observable
    public isLoadingContacts: boolean = false;

    @action
    public resetLoadingState = (): void => {
        this.isLoadingContacts = true;
    };

    @action
    public addSpeaker = (contact: IAccountContactDTO): void => {
        this.addSpeakers([contact]);
    };

    @action
    public addSpeakers = (contacts: IAccountContactDTO[]): void => {
        const addedContactIds = new Set(this.speakers.map(speaker => speaker.accountContactId));
        const addedSpeakers = contacts
            .filter(contact => !addedContactIds.has(contact.accountContactId))
            .map(contact => ({
                ...contact,
                positionOrder: 0,
            }));
        this.speakers = [...this.speakers, ...addedSpeakers];
        this.updateSpeakersOrder();
    };

    @action
    public confirmNewSpeaker = (contact: IAccountContactDTO): void => {
        this.addSpeaker(contact);
        this.closeAddSpeakerModal();
    };

    @action
    public removeSpeaker = (accountContactId: number): void => {
        this.speakers = this.speakers.filter(speaker => speaker.accountContactId !== accountContactId);
        this.updateSpeakersOrder();
    };

    @action
    public editSpeaker = (accountContactId: number): void => {
        this.editingSpeaker = this.speakers.find(speaker => speaker.accountContactId === accountContactId) ?? null;
        this.isAddSpeakerModalOpen = true;
    };

    @action
    public confirmEditSpeaker = (contact: IAccountContactDTO): void => {
        // Dispatch instead of mutating this.speakers/this.contacts directly,
        // so that the change is reflected in all other modules that may be using the same contact (e.g. a Speaker's own profile page)
        this.eventDispatcher.dispatch(EventName.SpeakerUpdated, contact);
        this.closeAddSpeakerModal();
    };

    public loadContacts = async (): Promise<void> => {
        runInAction(() => {
            this.isLoadingContacts = true;
        });
        try {
            const contacts = await this.accountContactsDAO.getAccountContacts(this.getAccountId()) ?? [];
            runInAction(() => {
                this.contacts = this.sortContactsByOrganization(contacts);
            });
        } catch (error) {
            const message = this.errorParsingService.extractMessage(error);
            alertServerError(errorText(), message);
        } finally {
            runInAction(() => {
                this.isLoadingContacts = false;
            });
        }
    };

    public getAccountId = (): number => {
        return parseInt(this.routingService.router.getState().params.accountId, RADIX_PARAM);
    };

    @action
    public openAddSpeakerModal = (): void => {
        this.editingSpeaker = null;
        this.isAddSpeakerModalOpen = true;
    };

    @action
    public closeAddSpeakerModal = (): void => {
        this.isAddSpeakerModalOpen = false;
        this.editingSpeaker = null;
    };

    public getModuleType = (): HubModuleType => {
        return HubModuleType.SPEAKERS;
    };

    public clone = (): SpeakersModuleVM => {
        const dto = this.isNew() ? this.toCreateDTO() : this.toUpdateDTO() as ISpeakersHubModuleDTO;
        const vm = SpeakersModuleVM.createFromDTO(dto);
        this.copyIdentityTo(vm);
        return vm;
    };

    public isValid = (): boolean => {
        return this.isVmValid();
    };

    public toCreateDTO = (): ICreateSpeakersHubModuleDTO => {
        const description = this.getTrimmedStringValue(DESCRIPTION);
        const transpiledDescription = transpileClassesToInlineStyles(description);

        return {
            positionOrder: this.getNumberValue(POSITION_ORDER),
            moduleType: HubModuleType.SPEAKERS,
            enabled: this.isEnabled(),
            label: this.getTrimmedStringValue(LABEL),
            displayLabel: this.getBooleanValue(DISPLAY_LABEL),
            description: transpiledDescription,
            layout: this.getFormVmValuePropsByKey(LAYOUT).value as HubModuleLayout.LIST,
            displayName: this.getBooleanValue(DISPLAY_NAME),
            displayTitle: this.getBooleanValue(DISPLAY_TITLE),
            displayOrg: this.getBooleanValue(DISPLAY_ORG),
            displayProfileImage: this.getBooleanValue(DISPLAY_PROFILE_IMAGE),
            displayBio: this.getBooleanValue(DISPLAY_BIO),
            speakers: this.speakers,
        };
    };

    // A speaker's own profile fields sync in independently (already persisted elsewhere), so they shouldn't count
    // as a pending change here — only membership/order are this module's own concern. toCreateDTO() is untouched.
    protected serialize = (): string => {
        const dto = this.toCreateDTO();
        return JSON.stringify({
            ...dto,
            speakers: dto.speakers.map(({ accountContactId, positionOrder }) => ({ accountContactId: accountContactId, positionOrder: positionOrder })),
        });
    };

    @action
    private handleSpeakerUpdated = (contact: IAccountContactDTO): void => {
        this.speakers = this.speakers.map(speaker =>
            speaker.accountContactId === contact.accountContactId ? { ...contact, positionOrder: speaker.positionOrder } : speaker
        );
        this.contacts = this.contacts.map(existingContact =>
            existingContact.accountContactId === contact.accountContactId
                ? { ...contact, organizationName: existingContact.organizationName }
                : existingContact
        );
    };

    private sortContactsByOrganization(contacts: IAccountContactListItemDTO[]): IAccountContactListItemDTO[] {
        return [...contacts].sort((a, b) => a.organizationName.localeCompare(b.organizationName));
    }

    private updateSpeakersOrder(): void {
        this.speakers.forEach((speaker, index) => {
            speaker.positionOrder = index;
        });
    }
}
